import { useCallback, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import useDebounce from './useDebounce'

type ParamValue = string | number | boolean | undefined | null

interface BaseParams {
    q: string
    page: number
    page_size: number
    sort_by: string
    sort_order: 'asc' | 'desc'
}

interface QueryParams {
    q: string
    page: number
    page_size: number
    sort_by: string
    sort_order: 'asc' | 'desc'
    filters: string
}

interface UseUrlSearchParamsOptions<TFilters> {
    /** Default values for base params */
    defaults?: Partial<BaseParams>
    /** Default values for filters */
    filterDefaults?: TFilters
    /** Debounce delay for search query (default: 300ms) */
    debounceDelay?: number
    /** Prefix for URL params to avoid conflicts when multiple tables exist (e.g., 'inv' -> 'inv_sort_by') */
    prefix?: string
}

interface SearchInputProps {
    value: string
    onChange: (value: string) => void
}

interface UseUrlSearchParamsReturn<TFilters> {
    /** Base params (q, page, page_size, sort_by, sort_order) */
    params: BaseParams
    /** Typed filters object */
    filters: TFilters
    /** Ready-to-use query params for API (with debounced q and encoded filters) */
    queryParams: QueryParams
    /** Set a single base param */
    setParam: (
        key: keyof BaseParams | string,
        value: ParamValue,
        resetPage?: boolean
    ) => void
    /** Set multiple base params */
    setParams: (updates: Partial<BaseParams>, resetPage?: boolean) => void
    /** Set a single filter */
    setFilter: <K extends keyof TFilters>(key: K, value: TFilters[K]) => void
    /** Set multiple filters */
    setFilters: (updates: Partial<TFilters>) => void
    /** Reset all params to defaults */
    resetParams: () => void
    /** Ready-to-use props for SearchInput component */
    searchInputProps: SearchInputProps
}

const DEFAULT_BASE_PARAMS: BaseParams = {
    q: '',
    page: 1,
    page_size: 20,
    sort_by: '',
    sort_order: 'desc',
}

/**
 * Hook to sync pagination, sorting, and filters with URL search params
 * Returns ready-to-use queryParams for API calls
 *
 * @example
 * const { params, filters, queryParams, setParam, setFilter } = useUrlSearchParams({
 *   defaults: { sort_by: 'created_at', page_size: 20 },
 *   filterDefaults: { employee_type: '', department_id: 0 }
 * })
 *
 * // Use queryParams directly for API
 * useQuery({ queryKey: ['employees', queryParams], queryFn: () => getEmployees(queryParams) })
 */
export function useUrlSearchParams<
    TFilters extends object = Record<string, never>,
>(
    options: UseUrlSearchParamsOptions<TFilters> = {}
): UseUrlSearchParamsReturn<TFilters> {
    const {
        defaults = {},
        filterDefaults = {} as TFilters,
        debounceDelay = 300,
        prefix = '',
    } = options
    const [searchParams, setSearchParams] = useSearchParams()

    const baseDefaults = { ...DEFAULT_BASE_PARAMS, ...defaults }

    // Parse base params from URL
    const params = useMemo((): BaseParams => {
        const parseValue = <T>(key: string, defaultValue: T): T => {
            const paramKey = prefix ? `${prefix}_${key}` : key
            const urlValue = searchParams.get(paramKey)
            if (urlValue === null) return defaultValue

            if (typeof defaultValue === 'number') return Number(urlValue) as T
            if (typeof defaultValue === 'boolean')
                return (urlValue === 'true') as T
            return urlValue as T
        }

        return {
            q: parseValue('q', baseDefaults.q),
            page: parseValue('page', baseDefaults.page),
            page_size: parseValue('page_size', baseDefaults.page_size),
            sort_by: parseValue('sort_by', baseDefaults.sort_by),
            sort_order: parseValue('sort_order', baseDefaults.sort_order) as
                | 'asc'
                | 'desc',
        }
    }, [
        searchParams,
        prefix,
        baseDefaults.q,
        baseDefaults.page,
        baseDefaults.page_size,
        baseDefaults.sort_by,
        baseDefaults.sort_order,
    ])

    // Debounced search query
    const debouncedQ = useDebounce(params.q, debounceDelay)

    // Parse filters from URL (stored as base64-encoded JSON)
    const filters = useMemo((): TFilters => {
        const filtersKey = prefix ? `${prefix}_filters` : 'filters'
        const filtersParam = searchParams.get(filtersKey)
        if (!filtersParam) return { ...filterDefaults }

        try {
            const decoded = atob(filtersParam)
            const parsed = JSON.parse(decoded)
            return { ...filterDefaults, ...parsed }
        } catch {
            return { ...filterDefaults }
        }
    }, [searchParams, filterDefaults, prefix])

    // Filters string for API (base64 encoded - same as URL param)
    // If URL has no filters param, encode filterDefaults
    const filtersString = useMemo(() => {
        const filtersKey = prefix ? `${prefix}_filters` : 'filters'
        const urlFilters = searchParams.get(filtersKey)
        if (urlFilters) return urlFilters

        // Apply filterDefaults when no URL filters - encode inline
        const cleanFilters: Record<string, unknown> = {}
        Object.entries(filterDefaults).forEach(([key, value]) => {
            // Skip empty values: undefined, null, empty string, 0, empty arrays
            if (
                value === undefined ||
                value === null ||
                value === '' ||
                value === 0
            )
                return
            if (Array.isArray(value) && value.length === 0) return
            cleanFilters[key] = value
        })
        if (Object.keys(cleanFilters).length === 0) return ''
        return btoa(JSON.stringify(cleanFilters))
    }, [searchParams, filterDefaults, prefix])

    // Ready-to-use query params for API
    const queryParams = useMemo(
        (): QueryParams => ({
            q: debouncedQ,
            page: params.page,
            page_size: params.page_size,
            sort_by: params.sort_by,
            sort_order: params.sort_order,
            filters: filtersString,
        }),
        [
            debouncedQ,
            params.page,
            params.page_size,
            params.sort_by,
            params.sort_order,
            filtersString,
        ]
    )

    // Keep filters ref for stable callbacks
    const filtersRef = useRef(filters)
    filtersRef.current = filters

    // Build base64-encoded filters string from object (only non-empty values)
    const buildFiltersString = useCallback(
        (filtersObj: Partial<TFilters>): string => {
            const cleanFilters: Record<string, unknown> = {}
            Object.entries(filtersObj).forEach(([key, value]) => {
                // Skip empty values: undefined, null, empty string, 0, empty arrays
                if (
                    value === undefined ||
                    value === null ||
                    value === '' ||
                    value === 0
                )
                    return
                if (Array.isArray(value) && value.length === 0) return
                cleanFilters[key] = value
            })
            if (Object.keys(cleanFilters).length === 0) return ''
            return btoa(JSON.stringify(cleanFilters))
        },
        []
    )

    // Set single base param (uses functional updater to avoid stale closures)
    const setParam = useCallback(
        (
            key: keyof BaseParams | string,
            value: ParamValue,
            resetPage = true
        ) => {
            setSearchParams(
                (prev) => {
                    const newParams = new URLSearchParams(prev)
                    const paramKey = prefix ? `${prefix}_${key}` : key
                    const pageKey = prefix ? `${prefix}_page` : 'page'

                    if (
                        value !== undefined &&
                        value !== null &&
                        value !== '' &&
                        value !== 0
                    ) {
                        newParams.set(paramKey, String(value))
                    } else {
                        newParams.delete(paramKey)
                    }

                    if (resetPage && key !== 'page') {
                        newParams.set(pageKey, '1')
                    }

                    return newParams
                },
                { replace: true }
            )
        },
        [setSearchParams, prefix]
    )

    // Set multiple base params at once
    const setParams = useCallback(
        (updates: Partial<BaseParams>, resetPage = true) => {
            setSearchParams(
                (prev) => {
                    const newParams = new URLSearchParams(prev)
                    const pageKey = prefix ? `${prefix}_page` : 'page'

                    Object.entries(updates).forEach(([key, value]) => {
                        const paramKey = prefix ? `${prefix}_${key}` : key
                        if (
                            value !== undefined &&
                            value !== null &&
                            value !== '' &&
                            value !== 0
                        ) {
                            newParams.set(paramKey, String(value))
                        } else {
                            newParams.delete(paramKey)
                        }
                    })

                    if (resetPage && !('page' in updates)) {
                        newParams.set(pageKey, '1')
                    }

                    return newParams
                },
                { replace: true }
            )
        },
        [setSearchParams, prefix]
    )

    // Set single filter (updates JSON filters param)
    const setFilter = useCallback(
        <K extends keyof TFilters>(key: K, value: TFilters[K]) => {
            const newFilters = { ...filtersRef.current, [key]: value }
            // Update ref immediately so consecutive sync calls see each other's changes
            filtersRef.current = newFilters as TFilters
            const filtersStr = buildFiltersString(newFilters)

            setSearchParams(
                (prev) => {
                    const newParams = new URLSearchParams(prev)
                    const filtersKey = prefix ? `${prefix}_filters` : 'filters'
                    const pageKey = prefix ? `${prefix}_page` : 'page'

                    if (filtersStr) {
                        newParams.set(filtersKey, filtersStr)
                    } else {
                        newParams.delete(filtersKey)
                    }

                    // Reset page when filter changes
                    newParams.set(pageKey, '1')

                    return newParams
                },
                { replace: true }
            )
        },
        [setSearchParams, buildFiltersString, prefix]
    )

    // Set multiple filters at once
    const setFilters = useCallback(
        (updates: Partial<TFilters>) => {
            const newFilters = { ...filtersRef.current, ...updates }
            const filtersStr = buildFiltersString(newFilters)

            setSearchParams(
                (prev) => {
                    const newParams = new URLSearchParams(prev)
                    const filtersKey = prefix ? `${prefix}_filters` : 'filters'
                    const pageKey = prefix ? `${prefix}_page` : 'page'

                    if (filtersStr) {
                        newParams.set(filtersKey, filtersStr)
                    } else {
                        newParams.delete(filtersKey)
                    }

                    // Reset page when filters change
                    newParams.set(pageKey, '1')

                    return newParams
                },
                { replace: true }
            )
        },
        [setSearchParams, buildFiltersString, prefix]
    )

    // Reset all params to defaults
    const resetParams = useCallback(() => {
        setSearchParams(new URLSearchParams(), { replace: true })
    }, [setSearchParams])

    // Search input handler
    const handleSearchChange = useCallback(
        (value: string) => {
            setSearchParams(
                (prev) => {
                    const newParams = new URLSearchParams(prev)
                    const qKey = prefix ? `${prefix}_q` : 'q'
                    const pageKey = prefix ? `${prefix}_page` : 'page'
                    if (value) {
                        newParams.set(qKey, value)
                    } else {
                        newParams.delete(qKey)
                    }
                    newParams.set(pageKey, '1')
                    return newParams
                },
                { replace: true }
            )
        },
        [setSearchParams, prefix]
    )

    // Ready-to-use props for SearchInput
    const searchInputProps: SearchInputProps = useMemo(
        () => ({
            value: params.q,
            onChange: handleSearchChange,
        }),
        [params.q, handleSearchChange]
    )

    return {
        params,
        filters,
        queryParams,
        setParam,
        setParams,
        setFilter,
        setFilters,
        resetParams,
        searchInputProps,
    }
}

export default useUrlSearchParams
